import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { Tooltip } from "../components/Tooltip";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import type { AiItemDto, ContractNature } from "../../autogen/bff-api";
import { tiptapToPlainText } from "../input/TipTap/tiptapToPlainText";
import { useCpvCodes } from "./useCpvCodes";
import { useCpvSuggestions } from "./useCpvSuggestions";

export type CpvSuggestionContext = {
  title: string;
  description: string;
  contractType?: ContractNature;
  item: AiItemDto;
};

type CpvSuggestionsProps = {
  context: CpvSuggestionContext;
  selected: string[];
  onAdd: (code: string) => void;
  isDisabled?: boolean;
};

export const CpvSuggestions = ({ context, selected, onAdd, isDisabled }: CpvSuggestionsProps) => {
  const { t, i18n } = useTranslation();
  const { codeIndex } = useCpvCodes();
  const isNorwegian = i18n.language.startsWith("no");
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const { suggest, suggestions, isLoading, hasError } = useCpvSuggestions({ codeIndex, item: context.item });
  const [isPanelShown, setIsPanelShown] = useState(true);
  const [suggestedContext, setSuggestedContext] = useState<string | null>(null);

  const availableSuggestions = useMemo(
    () => suggestions?.filter((s) => !selectedSet.has(s.code)) ?? [],
    [suggestions, selectedSet],
  );

  const description = useMemo(() => tiptapToPlainText(context.description), [context.description]);
  const hasContext = context.title.trim() !== "" || description !== "";
  const contextKey = [context.title.trim(), description, context.contractType ?? ""].join("\u0000");

  const isStale = suggestedContext !== null && suggestedContext !== contextKey;
  const isGenerate = suggestions === null || isStale;
  // The tooltip only has something to say while the button is disabled for want of a title.
  const needsTooltip = isGenerate && !hasContext;

  const handleSuggest = async () => {
    const sentContext = contextKey;
    setIsPanelShown(true);
    await suggest({
      title: context.title.trim(),
      description,
      contractType: context.contractType,
      language: isNorwegian ? "no" : "en",
    });
    setSuggestedContext(sentContext);
  };

  const buttonLabel = () => {
    if (suggestions === null) return t("Suggest CPV codes with AI");
    if (isStale) return t("Suggest again");
    return isPanelShown ? t("Hide AI suggestions") : t("Show AI suggestions");
  };

  return (
    <Box mt="3">
      {/* shouldWrapChildren puts a <span tabIndex={0}> around the button, which is how a disabled
          button can still raise a tooltip — and a tab stop for nothing the rest of the time. */}
      <Tooltip
        label={t("Add a title or description first")}
        isDisabled={!needsTooltip}
        shouldWrapChildren={needsTooltip}
      >
        <Button
          size="sm"
          variant="outline"
          colorScheme="teal"
          bg={!isGenerate && isPanelShown ? "smPrimarySofter" : undefined}
          leftIcon={<Icon as={FaMagic} />}
          onClick={isGenerate ? handleSuggest : () => setIsPanelShown((v) => !v)}
          isLoading={isLoading}
          loadingText={t("Suggesting…")}
          isDisabled={isDisabled || (isGenerate && !hasContext)}
        >
          {buttonLabel()}
        </Button>
      </Tooltip>

      {hasError && (
        <Text mt="2" fontSize="sm" color="smError">
          {t("Couldn't generate suggestions. Please try again.")}
        </Text>
      )}

      {suggestions && isPanelShown && (
        <Box mt="2" borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3" bg="smBackground">
          <Flex justify="space-between" align="center" mb="2">
            <Text fontSize="sm" fontWeight="semibold">
              {t("AI suggestions")}
            </Text>
            <Button variant="link" size="xs" colorScheme="teal" onClick={() => setIsPanelShown(false)}>
              {t("Dismiss")}
            </Button>
          </Flex>

          {suggestions.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("No suggestions found")}
            </Text>
          ) : availableSuggestions.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("All found suggestions have been added")}
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {availableSuggestions.map((s) => {
                const name = isNorwegian ? s.nameNb : s.nameEn;
                return (
                  <Flex key={s.code} align="flex-start" justify="space-between" gap="3">
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {s.code} — {name}
                      </Text>
                      {s.reason && (
                        <Text fontSize="xs" color="smMuted">
                          {s.reason}
                        </Text>
                      )}
                    </Box>
                    <Button
                      size="xs"
                      flexShrink={0}
                      colorScheme="teal"
                      variant="solid"
                      onClick={() => onAdd(s.code)}
                    >
                      {t("Add")}
                    </Button>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};
